import {
  CARD_REF, CardRules, CardsJson, CardsState, FUND_REF, GrantedItems, OfferDef, OfferItem, OfferScreen, SECONDS_PER_DAY, purchasesAllowed, scaleItems,
  signedFloorOf, utcDay,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { walletSection } from "./economyState";
import type { Clock } from "./ports";
import { NOT_SAVING_HERE } from "./purchases";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * Cards and the growth fund (MONETISATION_PLAN MP-01/02/03): the use cases over meta/cards.ts. A card or the fund is a
 * Deals offer, bought through the Store like any other (Deals.prepare → PurchaseService → Deals.grant, which pays its
 * items now and calls `granted` here); its daily items and milestones are claimed here, in the hub only (the never
 * list), each paid once with its own ref and added to its purchase's wallet record, so a refund takes back what is
 * still unspent of everything it paid (ADR 0013) and `revoked` cancels its remaining days. Days are UTC days of the
 * trusted clock (MP-13): the device's, never before the newest signed store date nor a day already seen. The state
 * is the `cards` save section.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const cardsSection = registerSaveSection<CardsJson>("cards", asObject, (v) => v);

/** The page ids the Deals strip shows them on (offers.json). */
export const CARDS_PAGE = "cards";
export const FUND_PAGE = "fund";
/** The fund page's one-time Deals badge, cleared once the page is opened. */
const FUND_SEEN = "fund";

/**
 * "Now" for anything a moved-back clock must not reopen (MP-13; cards, and the season pass and events may use it): the
 * device clock, never before the newest signed store date the save's wallet holds.
 */
export function trustedNowSeconds(save: HasSections, clock: Clock): number {
  return Math.max(clock.utcNowSeconds(), signedFloorOf(walletSection.read(save)));
}

/** Two grants as one receipt. */
export function mergeGranted(a: GrantedItems, b: GrantedItems): GrantedItems {
  return { gems: a.gems + b.gems, gold: a.gold + b.gold, speedUps: [...a.speedUps, ...b.speedUps], chests: [...a.chests, ...b.chests], gear: [...a.gear, ...b.gear], skins: [...a.skins, ...b.skins] };
}

export const NOTHING_GRANTED: GrantedItems = { gems: 0, gold: 0, speedUps: [], chests: [], gear: [], skins: [] };

/** A card as the Deals page and the hub chip show it. */
export interface CardView {
  readonly offerId: string;
  readonly name: string;
  /** Days one purchase adds. */
  readonly days: number;
  /** What each day pays. */
  readonly daily: readonly OfferItem[];
  readonly running: boolean;
  /** Today as the run's day, 1-based ("Day 4 of 30"); 0 when not running. */
  readonly day: number;
  /** The run's length (30; 60 once extended); 0 when not running. */
  readonly total: number;
  /** Days left, today included. */
  readonly daysLeft: number;
  /** 00:00 UTC after the run's last day (seconds); null when not running. */
  readonly endsAt: number | null;
  /** Days claimable now (today's and banked ones) and what claiming them pays. */
  readonly claimable: number;
  readonly claimItems: readonly OfferItem[];
  /** The next UTC midnight: when the next day opens. */
  readonly nextAt: number;
  /** A purchase is allowed now: not running, or in its last `rebuyWindowDays` days (it then extends the run). */
  readonly canBuy: boolean;
  readonly rebuyWindowDays: number;
  readonly bankDays: number;
}

export interface FundMilestoneView { readonly level: number; readonly items: readonly OfferItem[]; readonly reached: boolean; readonly claimed: boolean }

export interface FundView {
  readonly offerId: string;
  readonly name: string;
  readonly owned: boolean;
  readonly cleared: number;
  readonly milestones: readonly FundMilestoneView[];
  /** Reached and not yet paid (owned only). */
  readonly owed: number;
}

export interface CardsDeps {
  readonly catalog: LevelCatalog;
  readonly state: CardsState;
  /** Pays items into the profile and the wallet (Deals' grantItems path), seeded and logged by `ref`. */
  pay(items: readonly OfferItem[], ref: string, now: number): GrantedItems;
}

export class Cards {
  constructor(private readonly d: CardsDeps) {}

  get state(): CardsState { return this.d.state; }
  private get rules(): CardRules { return this.d.catalog.balance.offers.cardRules; }
  private get cleared(): number { return this.d.catalog.highestLevelCleared; }

  /** Today's UTC day as the card rules count it (MP-13). */
  today(now: number): number { return this.d.state.today(utcDay(this.d.catalog.economy.wallet.trustedNow(now))); }

  private offers(): OfferDef[] { return this.d.catalog.balance.offers.pages.flatMap((p) => p.offers); }
  cardDefs(): OfferDef[] { return this.offers().filter((o) => o.card !== null); }
  fundDefs(): OfferDef[] { return this.offers().filter((o) => o.fund !== null); }
  private def(offerId: string): OfferDef | null { return this.offers().find((o) => o.id === offerId) ?? null; }

  /** Notes today, so a clock moved back later reopens nothing (the hub calls it on each visit). True when it moved on. */
  noteVisit(now: number): boolean {
    const t = this.today(now);
    if (t <= this.d.state.seenDay) return false;
    this.d.state.noteDay(t);
    return true;
  }

  view(offerId: string, now: number): CardView | null {
    const def = this.def(offerId), s = this.d.state;
    if (!def?.card) return null;
    const today = this.today(now), running = s.active(offerId, today), end = s.endDay(offerId);
    const claimable = s.claimable(offerId, this.rules, today).length;
    return {
      offerId, name: def.name, days: def.card.days, daily: def.card.daily, running, day: s.dayNumber(offerId, today), total: running ? s.paidDays(offerId) : 0,
      daysLeft: running && end !== null ? end - today : 0, endsAt: running && end !== null ? end * SECONDS_PER_DAY : null,
      claimable, claimItems: scaleItems(def.card.daily, claimable), nextAt: (today + 1) * SECONDS_PER_DAY,
      canBuy: s.canBuy(offerId, this.rules, today), rebuyWindowDays: this.rules.rebuyWindowDays, bankDays: this.rules.bankDays,
    };
  }

  /** Every card, in catalog order. */
  views(now: number): CardView[] { return this.cardDefs().map((o) => this.view(o.id, now)!); }

  fundView(offerId: string): FundView | null {
    const def = this.def(offerId);
    if (!def?.fund) return null;
    const f = this.d.state.fund(offerId), owned = this.d.state.ownsFund(offerId), cleared = this.cleared;
    const milestones = def.fund.milestones.map((m) => ({ level: m.level, items: m.items, reached: cleared >= m.level, claimed: f?.claimed.includes(m.level) ?? false }));
    return { offerId, name: def.name, owned, cleared, milestones, owed: owned ? milestones.filter((m) => m.reached && !m.claimed).length : 0 };
  }

  /** Why a card or the fund cannot be bought now ("" when it can, or for any other offer): Deals' `blocked`. */
  blocked(offerId: string, now: number): string {
    const def = this.def(offerId);
    if (def?.card) return this.d.state.canBuy(offerId, this.rules, this.today(now)) ? "" : "card running";
    if (def?.fund) return this.d.state.ownsFund(offerId) ? "owned" : "";
    return "";
  }

  /** Card days claimable now, over every card (the hub chip's CLAIM). */
  claimableDays(now: number): number {
    const today = this.today(now);
    return this.cardDefs().reduce((n, o) => n + this.d.state.claimable(o.id, this.rules, today).length, 0);
  }

  /** Fund milestones reached and not yet paid, over every fund. */
  fundOwed(): number { return this.fundDefs().reduce((n, o) => n + this.d.state.fundOwed(o.id, o.fund!, this.cleared).length, 0); }

  /** The Deals pill's share: fund milestones to claim, and the fund's one-time "new" once it opens and before it is seen. */
  badge(): number {
    const page = this.d.catalog.balance.offers.pages.find((p) => p.id === FUND_PAGE);
    const fresh = page && this.cleared >= page.unlockAfterLevel && !this.d.state.hasSeen(FUND_SEEN) && !this.fundDefs().some((o) => this.d.state.ownsFund(o.id)) ? 1 : 0;
    return this.fundOwed() + fresh;
  }

  /** The fund page was opened: its "new" badge clears. Returns whether anything changed. */
  markFundSeen(): boolean { return this.d.state.markSeen(FUND_SEEN); }

  /** Claims a card's claimable days (hub only): each day paid once (`card:<id>:<day>`) and counted to its purchase. */
  claim(offerId: string, now: number, screen: OfferScreen = "hub"): GrantedItems | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const def = this.def(offerId);
    if (!def?.card) return "no such card";
    const days = this.d.state.claimable(offerId, this.rules, this.today(now));
    if (days.length === 0) return "nothing to claim";
    const payers = days.map((day) => this.d.state.payerOf(offerId, day));
    this.d.state.claim(offerId, this.rules, this.today(now));
    let out = NOTHING_GRANTED;
    days.forEach((day, i) => { out = mergeGranted(out, this.payDay(def.card!.daily, `${CARD_REF}${offerId}:${day}`, payers[i], now)); });
    return out;
  }

  /** The hub chip: every card's claimable days in one receipt; the names of the cards that paid. */
  claimAll(now: number, screen: OfferScreen = "hub"): { granted: GrantedItems; names: string[] } | string {
    if (!purchasesAllowed(screen)) return "not here";
    let granted = NOTHING_GRANTED;
    const names: string[] = [];
    for (const o of this.cardDefs()) {
      const r = this.claim(o.id, now, screen);
      if (typeof r === "string") { if (r !== "nothing to claim") return r; continue; }
      granted = mergeGranted(granted, r);
      names.push(o.name);
    }
    return names.length > 0 ? { granted, names } : "nothing to claim";
  }

  /** Claims one reached fund milestone (hub only), once. */
  claimFund(offerId: string, level: number, now: number, screen: OfferScreen = "hub"): GrantedItems | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const def = this.def(offerId);
    if (!def?.fund) return "no such fund";
    const items = this.d.state.claimFund(offerId, def.fund, level, this.cleared);
    if (typeof items === "string") return items;
    return this.payDay(items, `${FUND_REF}${offerId}:${level}`, this.d.state.fund(offerId)!.txn, now);
  }

  /**
   * A verified purchase Deals has just paid the items of (`at`: its signed date, seconds): a card starts on the UTC
   * day it arrives (the purchase's, unless delivered late) or extends its run; a fund pays every milestone already
   * reached. Returns what that paid on top (an ended run's banked days first, the reached milestones), or null.
   */
  granted(offerId: string, txn: string, at: number, now: number): GrantedItems | null {
    const def = this.def(offerId);
    if (def?.card) {
      const today = this.today(Math.max(now, at)), day = Math.max(utcDay(at), today);
      let out: GrantedItems | null = null;
      if (!this.d.state.active(offerId, day) && this.d.state.claimable(offerId, this.rules, today).length > 0) {
        const r = this.claim(offerId, now);
        if (typeof r !== "string") out = r;
      }
      this.d.state.activate(offerId, def.card, txn, day);
      return out;
    }
    if (def?.fund) {
      if (!this.d.state.buyFund(offerId, txn)) return null;
      let out = NOTHING_GRANTED;
      for (const level of this.d.state.fundOwed(offerId, def.fund, this.cleared)) {
        const r = this.claimFund(offerId, level, now);
        if (typeof r !== "string") out = mergeGranted(out, r);
      }
      return out;
    }
    return null;
  }

  /** A refund: the transaction's card days or fund stop paying. Returns whether it held any. */
  revoked(txn: string): boolean { return this.d.state.revoke(txn); }

  /** Pays one day or milestone and counts its gems to the purchase that paid for it (a refund takes them back). */
  private payDay(items: readonly OfferItem[], ref: string, payer: string | null, now: number): GrantedItems {
    const g = this.d.pay(items, ref, now);
    if (payer && g.gems > 0) this.d.catalog.economy.wallet.addPurchaseGems(payer, g.gems);
    return g;
  }
}
